import { useCallback, useEffect, useState } from "react";
import { CheckSquare, ListPlus, Loader2, Pencil, Plus, Square, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Panel } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { shortDate } from "@/lib/format";

const today = () => isoDay(new Date());

/** The checklist on a work item: add, rename, assign, date, tick, delete. */
export const WorkTasks = ({ item, canWrite, onChanged }) => {
  const [checklists, setChecklists] = useState([]);
  const [draft, setDraft] = useState({ name: "", assignee: "", due_date: "" });
  const [editing, setEditing] = useState(-1);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");
  const [listPick, setListPick] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/works/checklists");
      setChecklists(res.data.checklists || []);
    } catch { setChecklists([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tasks = item.event_detail?.task_list || [];
  const overdue = (t) => !t.done && t.due_date && t.due_date < today();

  const run = async (fn, message) => {
    setBusy("x");
    let ok = false;
    try {
      const res = await fn();
      toast.success(message);
      onChanged(res.data);
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  return (
    <Panel testId="work-tasks" kicker="Checklist"
           title={`Tasks · ${tasks.filter((t) => t.done).length}/${tasks.length} done`}
           actions={canWrite && checklists.length ? (
             <Select value={listPick} onValueChange={async (v) => {
               setListPick(v);
               await run(() => api.post(`/works/${item.id}/checklist`, { checklist: v }),
                         "Checklist added");
               setListPick("");
             }}>
               <SelectTrigger data-testid="work-checklist-select"
                              className="h-8 w-[190px] bg-muted/40 text-[11px]">
                 <ListPlus className="mr-1 h-3.5 w-3.5 text-primary" />
                 <SelectValue placeholder="Use a ready-made list" />
               </SelectTrigger>
               <SelectContent>
                 {checklists.map((c) => (
                   <SelectItem key={c.key} value={c.key}>
                     {c.label} · {c.tasks.length} tasks
                   </SelectItem>))}
               </SelectContent>
             </Select>
           ) : null}>
      <div className="divide-y divide-border">
        {tasks.length === 0 && (
          <div className="px-3 py-4 text-[11.5px] text-muted-foreground" data-testid="work-tasks-empty">
            No tasks yet. Add your own below, or drop in a ready-made list.
          </div>
        )}
        {tasks.map((t, i) => (
          <div key={`${t.name}-${i}`} className="px-3 py-2" data-testid={`work-task-${i}`}>
            {editing === i ? (
              <div className="space-y-2">
                <Input value={edit.name} data-testid="work-task-edit-name"
                       onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                       className="h-8 bg-muted/40 text-[11.5px]" />
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={edit.assignee} placeholder="Assign to (email or name)"
                         data-testid="work-task-edit-assignee"
                         onChange={(e) => setEdit({ ...edit, assignee: e.target.value })}
                         className="h-8 w-[210px] bg-muted/40 text-[11.5px]" />
                  <DateField value={edit.due_date} testId="work-task-edit-due"
                             placeholder="Due date"
                             onChange={(v) => setEdit({ ...edit, due_date: v })}
                             className="h-8 w-[150px] text-[11px]" />
                  <Button size="sm" disabled={busy === "x"} data-testid="work-task-edit-save"
                          onClick={async () => {
                            const ok = await run(() => api.put(`/works/${item.id}/tasks/${i}`, {
                              ...edit, clear_due_date: !edit.due_date,
                            }), "Task updated");
                            if (ok) setEditing(-1);
                          }}
                          className="h-8 rounded-full bg-primary px-3 text-[10px] text-primary-foreground hover:bg-primary/90">
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(-1)}
                          data-testid="work-task-edit-cancel"
                          className="h-8 rounded-full px-2 text-[10px] text-muted-foreground">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button type="button" disabled={!canWrite || busy === "x"}
                        data-testid={`work-task-toggle-${i}`}
                        onClick={() => run(() => api.put(`/works/${item.id}/tasks/${i}`,
                                                         { done: !t.done }),
                                           t.done ? "Task reopened" : "Task done")}>
                  {t.done ? <CheckSquare className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    : <Square className="h-3.5 w-3.5 text-muted-foreground/70" />}
                </button>
                <span className={`flex-1 text-[11.5px] ${t.done
                  ? "text-muted-foreground line-through" : "text-foreground/85"}`}>
                  {t.name}
                </span>
                {t.due_date && (
                  <Chip className={overdue(t)
                    ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                    : "border-border bg-muted text-muted-foreground"}>
                    {overdue(t) ? "overdue " : "due "}{shortDate(t.due_date)}
                  </Chip>
                )}
                <span className="w-[130px] truncate text-right font-mono text-[10px] text-muted-foreground">
                  {t.assignee || "unassigned"}
                </span>
                {canWrite && (
                  <>
                    <Button size="sm" variant="ghost" data-testid={`work-task-edit-${i}`}
                            onClick={() => {
                              setEditing(i);
                              setEdit({ name: t.name, assignee: t.assignee || "",
                                        due_date: t.due_date || "" });
                            }}
                            className="h-7 rounded-full px-1.5 text-muted-foreground">
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`work-task-delete-${i}`}
                            disabled={busy === "x"}
                            onClick={() => run(() => api.delete(`/works/${item.id}/tasks/${i}`),
                                               "Task removed")}
                            className="h-7 rounded-full px-1.5 text-red-600 dark:text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border p-3">
          <Input value={draft.name} placeholder="Add a task of your own"
                 data-testid="work-task-new-name"
                 onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                 className="h-8 min-w-[220px] flex-1 bg-muted/40 text-[11.5px]" />
          <Input value={draft.assignee} placeholder="Assign to"
                 data-testid="work-task-new-assignee"
                 onChange={(e) => setDraft({ ...draft, assignee: e.target.value })}
                 className="h-8 w-[170px] bg-muted/40 text-[11.5px]" />
          <DateField value={draft.due_date} testId="work-task-new-due" placeholder="Due date"
                     onChange={(v) => setDraft({ ...draft, due_date: v })}
                     className="h-8 w-[150px] text-[11px]" />
          <Button size="sm" disabled={busy === "x" || !draft.name.trim()}
                  data-testid="work-task-add"
                  onClick={async () => {
                    const ok = await run(() => api.post(`/works/${item.id}/tasks/add`, draft),
                                         "Task added");
                    if (ok) setDraft({ name: "", assignee: "", due_date: "" });
                  }}
                  className="h-8 rounded-full bg-primary px-3 text-[10px] text-primary-foreground hover:bg-primary/90">
            {busy === "x" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              : <Plus className="mr-1 h-3 w-3" />} Add
          </Button>
        </div>
      )}
    </Panel>
  );
};
